import React, { useCallback, useState } from 'react';
import { apiFetch } from './utils/api';
import { formatKrw } from './utils/currency';
import './RefundManager.css';

/**
 * PortOne Refund Manager — site admins only.
 *
 * Spec: .claude/TODO/portone-refund-manager.md — P2 (T2.5).
 *
 * WHAT THIS SCREEN IS CAREFUL ABOUT
 * ---------------------------------
 * Every design choice here exists because of a specific way this could refund
 * the wrong money:
 *
 *  - The customer's OWN purchases and purchases that merely FUNDED THEIR ORG are
 *    rendered as two separate sections. A purchase is made by a user but
 *    benefits an org; merging them invites refunding a colleague's payment, and
 *    the money goes back to the BUYER's instrument, not the searched person's.
 *
 *  - Unsettled attempts are shown, greyed. An abandoned intent at the same
 *    amount and date shadows a real one, and hiding it does not make it stop
 *    shadowing.
 *
 *  - The confirm step restates the buyer, the amount and the method, and
 *    requires the amount to be RETYPED. The dialog is the last point at which a
 *    wrong row is recoverable.
 *
 *  - Blockers from the server are rendered verbatim and disable the action.
 *    They include PortOne disagreeing with our ledger, which means somebody
 *    refunded outside this system.
 *
 * ⚠ The server enforces all of this independently. Nothing here is a security
 * control — the UI cannot be the thing that stops a bad refund.
 */
export default function RefundManager() {
  const [query, setQuery] = useState('');
  const [customers, setCustomers] = useState(null);
  const [selected, setSelected] = useState(null);
  const [tx, setTx] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const search = useCallback(async (e) => {
    e?.preventDefault();
    if (query.trim().length < 2) return;
    setBusy('search'); setError(''); setCustomers(null);
    setSelected(null); setTx(null); setLedger(null);
    try {
      const res = await apiFetch(`/api/v1/admin/refunds/customers?q=${encodeURIComponent(query.trim())}`);
      const d = await res.json().catch(() => ({}));
      if (res.ok) setCustomers(d.items || []);
      else setError(d.detail || 'Search failed.');
    } catch { setError('Network error.'); }
    setBusy('');
  }, [query]);

  const openCustomer = useCallback(async (c) => {
    setSelected(c); setTx(null); setLedger(null); setBusy('tx'); setError('');
    try {
      const res = await apiFetch(`/api/v1/admin/refunds/transactions?user_id=${encodeURIComponent(c.id)}`);
      const d = await res.json().catch(() => ({}));
      if (res.ok) setTx(d);
      else setError(d.detail || 'Could not load transactions.');
    } catch { setError('Network error.'); }
    setBusy('');
  }, []);

  const openLedger = useCallback(async (intentId) => {
    setLedger(null); setBusy('ledger'); setError('');
    try {
      const res = await apiFetch(`/api/v1/admin/refunds/ledger/${encodeURIComponent(intentId)}`);
      const d = await res.json().catch(() => ({}));
      if (res.ok) setLedger(d);
      else setError(d.detail || 'Could not load the ledger.');
    } catch { setError('Network error.'); }
    setBusy('');
  }, []);

  return (
    <div className="rfm">
      <header className="rfm-head">
        <h1>Refund Manager</h1>
        <p className="rfm-sub">
          Find a customer, review every transaction, and refund to the original payment method.
          Site administrators only. Every search and every refund is recorded.
        </p>
      </header>

      <form className="rfm-search" onSubmit={search}>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Email or name"
          aria-label="Search customers by email or name"
        />
        <button type="submit" disabled={busy === 'search' || query.trim().length < 2}>
          {busy === 'search' ? 'Searching…' : 'Search'}
        </button>
      </form>

      {error && <div className="rfm-error">{error}</div>}

      {customers && customers.length === 0 && (
        <p className="rfm-empty">No customer matches that.</p>
      )}

      {customers && customers.length > 0 && (
        <table className="rfm-table">
          <thead>
            <tr><th>Email</th><th>Name</th><th>Organisation</th><th>Settled purchases</th><th /></tr>
          </thead>
          <tbody>
            {customers.map((c) => (
              <tr key={c.id} className={selected?.id === c.id ? 'rfm-row-active' : ''}>
                <td>{c.email}</td>
                <td>{c.full_name || '—'}</td>
                <td>{c.org_name || '—'}</td>
                <td>{c.settled_count}</td>
                <td>
                  <button type="button" className="rfm-link" onClick={() => openCustomer(c)}>
                    View transactions
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {busy === 'tx' && <p className="rfm-empty">Loading transactions…</p>}

      {tx && (
        <>
          <TxSection
            title={`Purchases by ${selected?.email}`}
            rows={tx.own}
            onOpen={openLedger}
            emptyText="This person has made no purchase attempts."
          />
          {tx.org_funded.length > 0 && (
            <TxSection
              title={`Other purchases that funded ${selected?.org_name || 'this organisation'}`}
              rows={tx.org_funded}
              onOpen={openLedger}
              // Kept visually distinct: refunding one of these sends money back to
              // a DIFFERENT person's card than the one searched for.
              warn="Paid by someone else. A refund returns money to the original buyer, not to the person you searched for."
            />
          )}
        </>
      )}

      {busy === 'ledger' && <p className="rfm-empty">Reconciling with PortOne…</p>}
      {ledger && <Ledger ledger={ledger} onDone={() => openLedger(ledger.intent_id)} />}
    </div>
  );
}

function TxSection({ title, rows, onOpen, warn, emptyText }) {
  return (
    <section className="rfm-section">
      <h2>{title}</h2>
      {warn && <div className="rfm-warn">{warn}</div>}
      {rows.length === 0 ? (
        <p className="rfm-empty">{emptyText}</p>
      ) : (
        <table className="rfm-table">
          <thead>
            <tr>
              <th>Date</th><th>Product</th><th>Amount</th><th>Status</th>
              <th>Method</th><th>Refunded</th><th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.intent_id} className={r.settled ? '' : 'rfm-row-unsettled'}>
                <td>{(r.paid_at || r.created_at || '').slice(0, 10) || '—'}</td>
                <td>
                  <code>{r.product_code || r.product_type}</code>
                  {r.buyer_email && <div className="rfm-muted">{r.buyer_email}</div>}
                </td>
                <td>{formatKrw(r.amount_krw)}</td>
                <td>
                  <span className={`rfm-status rfm-status-${r.status}`}>{r.status}</span>
                  {/* The orphan signal. This is the credit-pack case: money was
                      taken but no payment record exists, so nothing could refund
                      it. Surfaced rather than hidden. */}
                  {r.settled && !r.payment_id && (
                    <div className="rfm-orphan">no payment record</div>
                  )}
                </td>
                <td>{r.pay_method || '—'}</td>
                <td>{r.refunded_krw > 0 ? formatKrw(r.refunded_krw) : '—'}</td>
                <td>
                  {r.settled && (
                    <button type="button" className="rfm-link" onClick={() => onOpen(r.intent_id)}>
                      Open
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function Ledger({ ledger, onDone }) {
  const L = ledger.ledger;
  const P = ledger.portone || {};
  const [amount, setAmount] = useState(String(L.ceiling_krw || ''));
  const [reason, setReason] = useState('');
  const [override, setOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [confirmAmount, setConfirmAmount] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);

  const requested = Number(amount);
  const policy = ledger.policy?.refundable_krw;
  const exceedsPolicy = policy != null && requested > policy;
  const exceedsCash = requested > L.ceiling_krw;

  const submit = useCallback(async () => {
    setErr(''); setSubmitting(true);
    try {
      const res = await apiFetch('/api/v1/admin/refunds/execute', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          intent_id: ledger.intent_id,
          amount_krw: requested,
          reason,
          override_policy: override,
          override_reason: override ? overrideReason : null,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { setDone(d); setConfirming(false); if (onDone) onDone(); }
      else setErr(d.detail || 'The refund could not be completed.');
    } catch { setErr('Network error. Do NOT retry until you have checked PortOne.'); }
    setSubmitting(false);
  }, [ledger.intent_id, requested, reason, override, overrideReason, onDone]);

  if (done) {
    return (
      <section className="rfm-section rfm-done">
        <h2>Refund completed</h2>
        <p>
          {formatKrw(done.amount_krw)} refunded. PortOne reference{' '}
          <code>{done.provider_refund_id || '—'}</code>.
        </p>
      </section>
    );
  }

  return (
    <section className="rfm-section rfm-ledger">
      <h2>{ledger.buyer_email} — <code>{ledger.product_code || ledger.product_type}</code></h2>
      <p className="rfm-muted">
        {ledger.org_name} · paid {(ledger.paid_at || '').slice(0, 10)} · {ledger.pay_method || 'method unknown'}
      </p>

      {/* The arithmetic, shown rather than asserted. */}
      <table className="rfm-ledger-table">
        <tbody>
          <tr><td>Paid</td><td>{formatKrw(L.paid_krw)}</td></tr>
          <tr><td>Already refunded</td><td>−{formatKrw(L.settled_refunds_krw)}</td></tr>
          {L.inflight_refunds_krw > 0 && (
            <tr><td>Refund in progress</td><td>−{formatKrw(L.inflight_refunds_krw)}</td></tr>
          )}
          <tr className="rfm-ledger-total">
            <td>Refundable</td><td>{formatKrw(L.ceiling_krw)}</td>
          </tr>
          {policy != null && (
            <tr className="rfm-muted">
              <td>Policy allows</td><td>{formatKrw(policy)}</td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="rfm-portone">
        <strong>PortOne</strong>{' '}
        {P.reachable
          ? <>{P.status} · total {formatKrw(P.total)} · cancelled {formatKrw(P.cancelled)} · cancellable {formatKrw(P.cancellable)}</>
          : <span className="rfm-no">unreachable — {P.error || 'no response'}</span>}
      </div>

      {ledger.blockers?.length > 0 && (
        <div className="rfm-blockers">
          {ledger.blockers.map((b, i) => <p key={i}>⚠ {b}</p>)}
        </div>
      )}

      {ledger.can_execute && !confirming && (
        <div className="rfm-form">
          <label>
            Amount to refund
            <input type="number" min="1" max={L.ceiling_krw} value={amount}
                   onChange={(e) => setAmount(e.target.value)} />
          </label>
          {exceedsCash && (
            <p className="rfm-hard">
              {formatKrw(requested)} is more than the {formatKrw(L.ceiling_krw)} that remains.
              This limit cannot be overridden — it is what was actually paid, less what has
              already gone back.
            </p>
          )}
          {exceedsPolicy && !exceedsCash && (
            <div className="rfm-override">
              <p>
                The refund policy allows {formatKrw(policy)}. Refunding {formatKrw(requested)}
                {' '}is an override and will be recorded as one.
              </p>
              <label>
                <input type="checkbox" checked={override}
                       onChange={(e) => setOverride(e.target.checked)} />
                {' '}Override the policy
              </label>
              {override && (
                <textarea rows={2} value={overrideReason}
                          onChange={(e) => setOverrideReason(e.target.value)}
                          placeholder="Why is this override justified?" />
              )}
            </div>
          )}
          <label>
            Reason (recorded)
            <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                      placeholder="Why is this being refunded?" />
          </label>
          <button
            type="button"
            className="rfm-btn"
            disabled={
              !requested || exceedsCash || reason.trim().length < 5
              || (exceedsPolicy && (!override || overrideReason.trim().length < 5))
            }
            onClick={() => { setConfirmAmount(''); setConfirming(true); }}
          >
            Refund {formatKrw(requested || 0)}
          </button>
        </div>
      )}

      {confirming && (
        <div className="rfm-confirm">
          <h3>Confirm</h3>
          {/* Restated in full. The buyer is named because on the org-funded
              section it is NOT the person who was searched for. */}
          <p>
            Refund <strong>{formatKrw(requested)}</strong> to <strong>{ledger.buyer_email}</strong>
            {' '}via <strong>{ledger.pay_method || 'the original method'}</strong>.
          </p>
          {override && <p className="rfm-warn">Policy override: {overrideReason}</p>}
          <label>
            Retype the amount to confirm
            <input type="text" value={confirmAmount}
                   onChange={(e) => setConfirmAmount(e.target.value)} />
          </label>
          {err && <p className="rfm-hard">{err}</p>}
          <div className="rfm-actions">
            <button type="button" className="rfm-btn-secondary"
                    onClick={() => setConfirming(false)}>Cancel</button>
            <button type="button" className="rfm-btn"
                    disabled={submitting || Number(confirmAmount) !== requested}
                    onClick={submit}>
              {submitting ? 'Refunding…' : 'Confirm refund'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
